import type { Locale, Role, Session } from '@sentra/contracts';
import { LOCALES } from '@sentra/contracts';
import { Languages, Menu, Moon, Search, Sun, UserRoundCog } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { ThemePreference } from '@/app/providers/theme';
import { useTheme } from '@/app/providers/theme';
import { Button } from '@/design/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/design/components/dropdown-menu';
import { useLocale } from '@/i18n';

/**
 * The header, whose one non-negotiable job is that the authenticated operator's name is always on
 * screen.
 *
 * AD-54: the gate is a shared terminal with rotating staff, and every audit row, override reason
 * and acknowledgment is attributed to whoever is signed in. A name tucked inside a menu is a name
 * nobody checks, and the failure it produces is silent — a whole shift recorded against the person
 * who opened the terminal that morning. So the name and the role are rendered as text in the header
 * itself, and the menu carries only the ACTIONS.
 *
 * The theme and language controls live here rather than in a preferences screen because both are
 * per-terminal choices somebody makes once, in the first minute, and then never again.
 */
export function Header({
  session,
  onOpenNavigation,
  onOpenPalette,
  onSwitchOperator,
  onSignOut,
}: Readonly<{
  session: Session;
  onOpenNavigation: () => void;
  onOpenPalette: () => void;
  onSwitchOperator: () => void;
  onSignOut: () => void;
}>): ReactNode {
  const { t } = useTranslation();
  const { locale, setLocale } = useLocale();
  const { preference, resolved, setPreference } = useTheme();

  return (
    <header className="print-chrome flex h-14 items-center gap-2 border-b bg-surface px-3">
      <Button
        variant="ghost"
        size="icon"
        className="md:hidden"
        aria-label={t('shell.openNavigation')}
        onClick={onOpenNavigation}
      >
        <Menu aria-hidden />
      </Button>

      <Button
        variant="outline"
        size="sm"
        className="gap-2 text-muted-foreground"
        onClick={onOpenPalette}
      >
        <Search aria-hidden className="size-4" />
        <span>{t('palette.open')}</span>
        <kbd className="ml-2 hidden rounded border px-1.5 py-0.5 text-2xs sm:inline">
          {paletteShortcut()}
        </kbd>
      </Button>

      <div className="ml-auto flex items-center gap-3">
        {/*
          Always visible, never behind a disclosure. See the note above: this is AD-54's
          "the authenticated name always visible", and it is the whole reason this header exists.
        */}
        <p className="hidden text-right text-xs leading-tight sm:block">
          <span className="block text-muted-foreground">{t('session.signedInAs')}</span>
          <span className="block font-medium text-foreground">{session.user.displayName}</span>
        </p>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              data-testid="account-menu"
              aria-label={t('session.accountMenu')}
            >
              <UserRoundCog aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel className="font-normal">
              <span className="block font-medium">{session.user.displayName}</span>
              <span className="block text-xs text-muted-foreground">
                {t(roleLabelKey(session.user.role))}
              </span>
              <span className="block text-xs text-muted-foreground">{session.user.email}</span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />

            <DropdownMenuLabel className="text-xs text-muted-foreground">
              {t('theme.label')}
            </DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={preference}
              onValueChange={(next) => {
                setPreference(next as ThemePreference);
              }}
            >
              <DropdownMenuRadioItem value="light" data-theme-choice="light">
                <Sun aria-hidden className="size-4" />
                {t('theme.light')}
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="dark" data-theme-choice="dark">
                <Moon aria-hidden className="size-4" />
                {t('theme.dark')}
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="system" data-theme-choice="system">
                {resolved === 'dark' ? (
                  <Moon aria-hidden className="size-4" />
                ) : (
                  <Sun aria-hidden className="size-4" />
                )}
                {t('theme.system')}
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />

            <DropdownMenuLabel className="text-xs text-muted-foreground">
              {t('language.label')}
            </DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={locale}
              onValueChange={(next) => {
                setLocale(next as Locale);
              }}
            >
              {LOCALES.map((candidate) => (
                // The language as a closed-set token, for the reason the gate's direction
                // buttons carry `data-direction`: the label is the language's own name, in that
                // language, and the value is not.
                <DropdownMenuRadioItem key={candidate} value={candidate} data-locale={candidate}>
                  <Languages aria-hidden className="size-4" />
                  {t(`language.${candidate}`)}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />

            <DropdownMenuItem onSelect={onSwitchOperator}>
              {t('session.switchOperator')}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={onSignOut}>{t('session.signOut')}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

/** The chord that opens the command palette, written the way the platform writes it. */
/**
 * The chord the palette hint shows, chosen by platform.
 *
 * `usePaletteShortcut` accepts `metaKey || ctrlKey`, so the BEHAVIOUR is right everywhere; what was
 * wrong was the hint, which said `⌘K` to a Linux or Windows gate terminal whose operator has no
 * such key. Read from the platform string rather than from a catalogue, because it is a key cap
 * rather than a sentence: it is the same glyph in both languages and translating it would invite
 * somebody to translate it differently.
 */
function paletteShortcut(): string {
  const { platform } = globalThis.navigator;
  return /mac|iphone|ipad/iu.test(platform) ? '⌘K' : 'Ctrl+K';
}

/**
 * The catalogue key for a role, as a total function rather than a lookup that can miss.
 *
 * Exported for the shell's section refusal, which names the role in the same words this header
 * does, so the two can never call one role two different things.
 */
export function roleLabelKey(
  role: Role,
): 'session.roleOperator' | 'session.roleAnalyst' | 'session.roleAdmin' | 'session.roleSuperAdmin' {
  switch (role) {
    case 'operator': {
      return 'session.roleOperator';
    }
    case 'analyst': {
      return 'session.roleAnalyst';
    }
    case 'admin': {
      return 'session.roleAdmin';
    }
    case 'super_admin': {
      return 'session.roleSuperAdmin';
    }
  }
}
